// testCleanup.ts
// Utilities for cleaning up test data after automated tests

import { Platform } from 'react-native';
import { auth, firestore as db, doc, deleteDoc } from '../config/firebase';
import { PrivateProfileService } from '../services/privateProfileService';

/**
 * Test Cleanup Utilities
 * Helps prevent test data pollution in Firestore
 */
export class TestCleanup {
  /**
   * Delete a test user profile from Firestore
   * Only works for profiles that look like test data
   */
  static async deleteTestProfile(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Safety check - only delete if userId looks like test data
      if (!userId.startsWith('test_') && !userId.includes('anonymous')) {
        return {
          success: false,
          error: 'Refusing to delete non-test profile for safety',
        };
      }

      const docRef = doc(db, 'privateProfile', userId);
      await deleteDoc(docRef);

      // Also invalidate cache
      await PrivateProfileService.invalidateProfileCache(userId);

      console.log(`🗑️ Deleted test profile: ${userId}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting test profile:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Sign out current test user
   */
  static async signOutTestUser(): Promise<{ success: boolean; error?: string }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: true }; // Already signed out
      }

      if (Platform.OS === 'web') {
        const { signOut } = require('firebase/auth');
        
        // Sign out from v12 modular auth
        await signOut(auth);
        
        // Also sign out from v5 compat auth (if it was loaded for OAuth)
        if ((window as any).firebase?.auth) {
          await (window as any).firebase.auth().signOut();
          console.log('✅ Signed out from Firebase v5 compat (test cleanup)');
        }
      } else {
        await auth.signOut();
      }

      console.log(`🚪 Signed out user: ${user.uid}`);
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Clean up after test run
   * Deletes current user profile if it's a test user and signs out
   */
  static async cleanupAfterTests(): Promise<{ success: boolean; profileDeleted: boolean; signedOut: boolean; error?: string }> {
    try {
      const user = auth.currentUser;
      let profileDeleted = false;
      let signedOut = false;

      // Delete profile if it's a test user
      if (user && user.isAnonymous) {
        const deleteResult = await this.deleteTestProfile(user.uid);
        profileDeleted = deleteResult.success;
        
        if (!deleteResult.success) {
          console.warn(`⚠️ Could not delete test profile: ${deleteResult.error}`);
        }
      }

      // Sign out
      const signOutResult = await this.signOutTestUser();
      signedOut = signOutResult.success;

      return {
        success: true,
        profileDeleted,
        signedOut,
      };
    } catch (error) {
      console.error('Error during cleanup:', error);
      return {
        success: false,
        profileDeleted: false,
        signedOut: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

